import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageBanner } from "@/components/site/PageBanner";
import { SectionHeading } from "@/components/site/SectionHeading";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { api, type CommunitySummary } from "@/lib/api";
import { formatAEDCompact } from "@/lib/format";
import { SafeImage } from "@/components/site/SafeImage";
import {
  COMMUNITIES,
  findCommunityContent,
  findCommunityPhoto,
  type CommunityContent,
} from "@/lib/communities";
import { useSeo } from "@/hooks/useSeo";

type MergedCommunity = CommunitySummary & {
  content?: CommunityContent;
  isEditorial?: boolean;
};

const EDITORIAL_ORDER = new Map(COMMUNITIES.map((c, index) => [c.slug, index]));

export function AreasPage() {
  const { version } = useLiveProperties();
  const [communities, setCommunities] = useState<CommunitySummary[]>([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Dubai Residential Communities & Neighbourhood Guide | Hive Homes",
    description:
      "Explore 10 Dubai communities — JVC, Business Bay, Tilal Al Ghaf, Damac Lagoons, Arjan and more. Live listings and prices per neighbourhood. Hive Homes boutique brokerage.",
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listCommunities()
      .then(({ communities }) => !cancelled && setCommunities(communities))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [version]);

  const merged = useMemo(
    () => mergeCommunities(communities).sort(sortCommunities),
    [communities],
  );
  const totalLiveProperties = communities.reduce((n, c) => n + c.count, 0);

  return (
    <>
      {/* Hero — client banner photo behind the page heading */}
      <PageBanner
        image="/images/site/communities.jpg"
        alt="Aerial view of a Dubai waterfront residential community with the city skyline behind"
      >
        <SectionHeading
          as="h1"
          eyebrow="Where We Operate"
          size="xl"
          title="Dubai's Finest Communities."
          body="Every community is different. Our advisors are here to guide you to the perfect match."
        />

        {/* Live stats line — fed by the same store as the listings. */}
        {!loading && (
          <p className="mt-7 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 border-t border-[var(--line)] pt-5 text-[13px] uppercase tracking-[0.16em] text-[var(--text-muted)] md:mt-8">
            <span className="font-display text-[20px] tracking-normal text-[var(--gold-bright)] tabular-nums">
              {String(merged.length).padStart(2, "0")}
            </span>
            Communities
            <span aria-hidden className="px-1 text-[rgba(240,234,216,0.4)]">·</span>
            <span className="font-display text-[20px] tracking-normal text-[var(--gold-bright)] tabular-nums">
              {String(totalLiveProperties).padStart(2, "0")}
            </span>
            Live Properties
          </p>
        )}
      </PageBanner>

      <section className="container-wide py-10 md:py-14 short:py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--line)] pb-5">
          <div>
            <p className="eyebrow">Directory</p>
            <h2
              className="mt-3 max-w-[18ch] font-display text-[30px] font-semibold leading-[1.03] tracking-[-0.02em] text-[var(--text)] sm:text-[38px] md:text-[46px]"
              style={{ fontVariationSettings: '"opsz" 72' }}
            >
              Explore All Communities.
            </h2>
          </div>
          {!loading && (
            <p className="meta uppercase tracking-[0.18em] text-[var(--text-muted)]">
              {String(merged.length).padStart(2, "0")} areas
            </p>
          )}
        </div>

        {loading ? (
          <GallerySkeleton />
        ) : (
          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {merged.map((community) => (
              <GalleryCard key={community.slug} community={community} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function mergeCommunities(communities: CommunitySummary[]): MergedCommunity[] {
  const editorialBySlug = new Map(COMMUNITIES.map((c) => [c.slug, c]));
  const liveSlugs = new Set(communities.map((c) => c.slug));

  return [
    ...communities.map((community) => {
      const content =
        editorialBySlug.get(community.slug) ??
        findCommunityContent(community.name);
      return {
        ...community,
        content,
        coverImage:
          content?.fallbackCover ??
          findCommunityPhoto(community.slug) ??
          community.coverImage,
      };
    }),
    ...COMMUNITIES.filter((community) => !liveSlugs.has(community.slug)).map((content) => ({
      name: content.name,
      slug: content.slug,
      count: 0,
      minPrice: 0,
      coverImage: content.fallbackCover,
      listingTypes: [] as ("sale" | "rent" | "off-plan")[],
      latestListedAt: "",
      content,
      isEditorial: true,
    })),
  ];
}

function sortCommunities(a: MergedCommunity, b: MergedCommunity): number {
  const editorialA = EDITORIAL_ORDER.get(a.slug) ?? 999;
  const editorialB = EDITORIAL_ORDER.get(b.slug) ?? 999;
  if (editorialA !== editorialB) return editorialA - editorialB;
  if (b.count !== a.count) return b.count - a.count;
  return a.name.localeCompare(b.name);
}

function targetFor(community: MergedCommunity): string {
  return community.content
    ? `/areas/${community.content.slug}`
    : `/properties?community=${encodeURIComponent(community.name)}`;
}

const LISTING_TYPE_LABELS: Record<"sale" | "rent" | "off-plan", string> = {
  sale: "Sale",
  rent: "Rent",
  "off-plan": "Off-Plan",
};

/**
 * Gallery community card — cover photo, sale/rent/off-plan mix, name,
 * and "From" price. No tag chips; the picture does the selling.
 */
function GalleryCard({ community }: { community: MergedCommunity }) {
  return (
    <li className="min-w-0">
      <Link
        to={targetFor(community)}
        data-on-dark
        className="group relative isolate flex min-h-[280px] overflow-hidden border border-[rgba(211,197,150,0.18)] bg-[var(--navy)] shadow-[0_20px_60px_-46px_rgba(13,20,34,0.45)] sm:min-h-[300px]"
      >
        {community.coverImage ? (
          <SafeImage
            src={community.coverImage}
            alt={`${community.name}, Dubai`}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-[0.84] transition-transform duration-[900ms] group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--navy)]">
            <span className="font-display text-6xl text-[var(--gold-bright)]/40">
              {community.name.charAt(0)}
            </span>
          </div>
        )}
        <span
          aria-hidden
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(13,20,34,0.10)_0%,rgba(13,20,34,0.20)_40%,rgba(13,20,34,0.86)_100%)]"
        />

        <div className="relative flex min-h-full w-full flex-col justify-end p-4 sm:p-5">
          {community.listingTypes.length > 0 && (
            <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.16em] text-[var(--cream)]/85">
              {community.listingTypes
                .map((t) => LISTING_TYPE_LABELS[t])
                .join(" · ")}
            </p>
          )}
          <h3
            className="font-display text-[25px] leading-[1.05] text-[var(--text-on-dark)] transition-colors duration-200 group-hover:text-[var(--gold-bright)] sm:text-[28px]"
            style={{ fontVariationSettings: '"opsz" 72' }}
          >
            {community.name}
          </h3>
          <div className="mt-4 flex items-end justify-between gap-3 border-t border-[rgba(211,197,150,0.24)] pt-3">
            <span className="block text-[13px] text-[var(--cream)]">
              {community.minPrice > 0
                ? `From ${formatAEDCompact(community.minPrice)}`
                : "Ask Hive Homes"}
            </span>
            <ArrowRight
              className="size-5 flex-none text-[var(--cream)] transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[var(--gold-bright)]"
              aria-hidden
            />
          </div>
        </div>
      </Link>
    </li>
  );
}

function GallerySkeleton() {
  return (
    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 9 }).map((_, index) => (
        <div
          key={index}
          className="min-h-[280px] animate-pulse bg-[var(--surface-2)] sm:min-h-[300px]"
        />
      ))}
    </div>
  );
}
